import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/class-control"
import { Loader2 } from "lucide-react"

const mainClass = cn(
  "inline-flex items-center justify-center gap-slim whitespace-nowrap rounded-md text-sm font-medium ring-offset-background", 
  "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1", 
  "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
)

const buttonVariants = cva(
  mainClass,
  {
    variants: {
      variant: {
        default: "bg-primary hover:bg-purple-500",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90",
        outline:
          "border border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white",
        secondary:
          "bg-purple-500 text-secondary hover:bg-purple-600",
        ghost: "hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-btn-default px-btn-x py-btn-y text-sm",
        sm: "h-btn-slim px-btn-x-slim py-btn-y-slim text-sm",
        lg: "h-btn-lg px-btn-x-lg py-btn-y-lg text-base",
        icon: "h-btn-default w-btn-default",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean,
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild =
    false, isLoading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({
          variant, size, className
        }))}
        {...props}
        disabled={props.disabled || isLoading}
        children={
          isLoading ? <Loader2 className="animate-spin" /> : props.children
        }
        ref={ref}
      />
    )
  }
)
Button.displayName = "Button"
// eslint-disable-next-line react-refresh/only-export-components

export { Button, buttonVariants }
